<template>
  <div class="modifiers-page">
    <header class="modifiers-page__header">
      <div>
        <h1>{{ $t('catalog.modifiers.title') }}</h1>
        <p>{{ $t('catalog.modifiers.subtitle') }}</p>
      </div>
      <button type="button" class="modifiers-page__btn" @click="openCreate">
        <i class="mdi mdi-plus"></i>
        {{ $t('catalog.modifiers.add') }}
      </button>
    </header>

    <div v-if="pagedModifiers.length" class="modifiers-page__grid">
      <article
        v-for="modifier in pagedModifiers"
        :key="modifier.id"
        class="modifier-card"
        :class="{ 'modifier-card--off': !modifier.is_active }"
      >
        <div class="modifier-card__top">
          <div class="modifier-card__icon">
            <i class="mdi mdi-plus-box-multiple-outline"></i>
          </div>
          <div class="modifier-card__actions">
            <button
              type="button"
              class="modifier-card__icon-btn"
              :title="modifier.is_active ? $t('catalog.modifiers.turnOff') : $t('catalog.modifiers.turnOn')"
              @click="toggleActive(modifier)"
            >
              <i :class="['mdi', modifier.is_active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline']"></i>
            </button>
            <button
              type="button"
              class="modifier-card__icon-btn"
              :title="$t('catalog.common.edit')"
              @click="openEdit(modifier)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="modifier-card__icon-btn modifier-card__icon-btn--danger"
              :title="$t('catalog.common.delete')"
              @click="askDelete(modifier)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>
        <h3>{{ modifier.name }}</h3>
        <div class="modifier-card__value">
          <strong>+{{ formatPrice(modifier.price) }} ₸</strong>
          <span v-if="!modifier.is_active" class="modifier-card__status">{{ $t('catalog.modifiers.inactive') }}</span>
        </div>
      </article>
    </div>

    <div v-else class="modifiers-page__empty">
      <i class="mdi mdi-plus-box-multiple-outline"></i>
      <p>{{ $t('catalog.modifiers.empty') }}</p>
      <span>{{ $t('catalog.modifiers.emptyHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="modifiers-page__pagination">
      <v-pagination v-model="page" :length="pageCount" :total-visible="5" color="#0b4f8a" />
    </div>

    <v-dialog v-model="formOpen" max-width="480">
      <v-card class="modifiers-page__dialog">
        <v-card-title>{{ isEdit ? $t('catalog.modifiers.editTitle') : $t('catalog.modifiers.createTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.name"
            :label="$t('catalog.common.name')"
            outlined
            dense
            class="mb-2"
            :placeholder="$t('catalog.modifiers.namePlaceholder')"
          />
          <v-text-field
            v-model="form.price"
            :label="$t('catalog.modifiers.price')"
            suffix="₸"
            outlined
            dense
            type="number"
            min="0"
          />
          <p class="modifiers-page__hint">{{ $t('catalog.modifiers.priceHint') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="save">{{ $t('catalog.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420">
      <v-card class="modifiers-page__dialog">
        <v-card-title>{{ $t('catalog.modifiers.deleteTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('catalog.modifiers.deleteDescription', { name: deleteName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">{{ $t('catalog.common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createModifier,
  deleteModifier,
  getModifiers,
  updateModifier,
} from '@/api/modifiers'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12

export default {
  name: 'ModifierControl',
  data() {
    return {
      modifiers: [],
      page: 1,
      formOpen: false,
      isEdit: false,
      modifierId: null,
      form: {
        name: '',
        price: '',
      },
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.modifiers.length / PER_PAGE) || 0
    },
    pagedModifiers() {
      const start = (this.page - 1) * PER_PAGE
      return this.modifiers.slice(start, start + PER_PAGE)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('ru-RU').format(Number(value) || 0)
    },
    async load() {
      try {
        this.modifiers = await getModifiers()
        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.modifierId = null
      this.form = { name: '', price: '' }
      this.formOpen = true
    },
    openEdit(modifier) {
      this.isEdit = true
      this.modifierId = modifier.id
      this.form = {
        name: modifier.name,
        price: modifier.price,
      }
      this.formOpen = true
    },
    askDelete(modifier) {
      this.modifierId = modifier.id
      this.deleteName = modifier.name
      this.deleteOpen = true
    },
    async toggleActive(modifier) {
      try {
        await updateModifier(modifier.id, {
          name: modifier.name,
          price: modifier.price,
          is_active: !modifier.is_active,
          sort_order: modifier.sort_order || 0,
        })
        notify(modifier.is_active
          ? this.$t('catalog.modifiers.turnedOff')
          : this.$t('catalog.modifiers.turnedOn'))
        await this.load()
      } catch (error) {
        notifyError(error)
      }
    },
    async save() {
      if (!String(this.form.name || '').trim()) {
        notify(this.$t('catalog.common.name'), 'warning')
        return
      }
      if (this.form.price === '' || this.form.price === null || Number(this.form.price) < 0) {
        notify(this.$t('catalog.modifiers.enterPrice'), 'warning')
        return
      }

      this.saving = true
      try {
        const payload = {
          name: String(this.form.name).trim(),
          price: Number(this.form.price),
          is_active: true,
        }
        if (this.isEdit) {
          const current = this.modifiers.find((item) => item.id === this.modifierId)
          payload.is_active = current ? !!current.is_active : true
          payload.sort_order = (current && current.sort_order) || 0
          await updateModifier(this.modifierId, payload)
        } else {
          await createModifier(payload)
        }
        notify(this.$t('catalog.common.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deleteModifier(this.modifierId)
        notify(this.$t('catalog.modifiers.deleted'))
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('catalog.modifiers.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.modifiers-page {
  padding: 8px 4px 32px;
}

.modifiers-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.modifiers-page__header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #152033;
}

.modifiers-page__header p {
  margin: 6px 0 0;
  color: #6b7a90;
}

.modifiers-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.modifiers-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

.modifier-card {
  padding: 16px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: #fff;
}

.modifier-card--off {
  opacity: 0.72;
}

.modifier-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.modifier-card__icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: #edf6fd;
  color: #0b4f8a;
  font-size: 22px;
}

.modifier-card__actions {
  display: flex;
  gap: 4px;
}

.modifier-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f4f7fb;
  color: #52627a;
  cursor: pointer;
}

.modifier-card__icon-btn--danger {
  color: #c62828;
}

.modifier-card h3 {
  margin: 0 0 8px;
  font-size: 17px;
  color: #152033;
}

.modifier-card__value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.modifier-card__value strong {
  font-size: 20px;
  color: #0b4f8a;
}

.modifier-card__status {
  font-size: 12px;
  color: #8a97ab;
}

.modifiers-page__empty {
  padding: 48px 20px;
  border: 1px dashed #cfd9e6;
  border-radius: 16px;
  text-align: center;
  color: #6b7a90;
}

.modifiers-page__empty i {
  font-size: 42px;
  color: #0b4f8a;
}

.modifiers-page__empty p {
  margin: 12px 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #152033;
}

.modifiers-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.modifiers-page__dialog {
  border-radius: 16px !important;
}

.modifiers-page__hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #6b7a90;
}

@media (max-width: 720px) {
  .modifiers-page__header {
    flex-direction: column;
  }

  .modifiers-page__btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
